import { getInvestmentsTableColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investmentsTableColumns.tsx';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useTableColPreferences } from '@dealstack/frontend/shared/hooks/useTableColPreferences/useTableColPreferences.hook.ts';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { getSecuritiesDataForMip } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { useLocation } from 'react-router-dom';
import { useMantineTableWithPaginationFilteringSorting } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import {
	ClassifierType,
	GetInvestmentsForMipItem,
	GetInvestmentsInvestorInfo,
	InvestmentsListQueryDto,
	InvestmentStats,
	SortDto,
} from '@dealstack/shared/lib/generated';
import { useMipClassifiersList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useMipClassifiers.ts';
import {
	EligibilityFilter,
	ManageInvestmentsTableColumn,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.types.ts';
import { useInvestmentsPermissions } from './useInvestmentsPermissions.ts';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import {
	getInvestmentOrderedIds,
	getInvestments,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/apis/useInvestments.ts';
import { mapInvestmentsTableFilterToDto } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investments-filter-map.ts';

interface Props {
	onKycClick?: (kycWorkflowId: string) => void;
	onInvestorDataClick?: (kycWorkflowId: string) => void;
	onTaxComplianceClick?: (kycWorkflowId: string) => void;
	onInviteClick?: (investor: GetInvestmentsInvestorInfo) => void;
	onRevokeClick?: (investmentWorkflowId: string) => void;
	onReleaseClick?: (investorId: string) => void;
	onVestingTermsClick?: (investorId: string) => void;
	eligibilityFilter?: EligibilityFilter | null;
	forDetailsNavigation?: boolean;
}

export const useInvestmentsTable = (props?: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const [footerStats, setFooterStats] = useState<InvestmentStats>();

	const { hasEditorPermission, hasKycAdminPermission } = useInvestmentsPermissions();
	const { disableSendInvestorInvitation } = useMIPManageContext();
	const { pathname, search } = useLocation();

	const { data: securities } = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, {
				mipId,
			}),
		],
		queryFn: ({ signal }) =>
			getSecuritiesDataForMip(
				formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, {
					mipId,
				}),
				undefined,
				signal
			),
		enabled: !!mipId,
	});

	const securitiesOptions = useMemo(
		() =>
			securities?.map((security) => ({
				label: security.name,
				value: security.id,
			})),
		[securities]
	);

	// Shared cohorts select: same source as the Investors table filter.
	const { data: mipClassifiers = [] } = useMipClassifiersList({
		mipId,
		isArchived: false,
		classifiedType: ClassifierType.INVESTOR_COHORT,
		enabled: !!mipId,
	});

	const mipClassifierFilterOptions = useMemo(
		() => mipClassifiers.map((c) => ({ label: c.name, value: c.id })),
		[mipClassifiers]
	);

	const columns = useMemo(
		() =>
			getInvestmentsTableColumns({
				tMipManage,
				tCommonUi,
				tCapManage,
				dealId,
				mipId,
				securitiesOptions,
				mipClassifierFilterOptions,
				onKycClick: props?.onKycClick,
				onInvestorDataClick: props?.onInvestorDataClick,
				onTaxComplianceClick: props?.onTaxComplianceClick,
				onInviteClick: props?.onInviteClick,
				onRevokeClick: props?.onRevokeClick,
				onReleaseClick: props?.onReleaseClick,
				onVestingTermsClick: props?.onVestingTermsClick,
				hasEditorPermission,
				hasKycAdminPermission,
				disableSendInvestorInvitation,
				currentPath: pathname + search,
				stats: footerStats,
			}),
		[
			tMipManage,
			tCommonUi,
			tCapManage,
			dealId,
			mipId,
			securitiesOptions,
			mipClassifierFilterOptions,
			props?.onKycClick,
			props?.onInvestorDataClick,
			props?.onTaxComplianceClick,
			props?.onInviteClick,
			props?.onRevokeClick,
			props?.onReleaseClick,
			props?.onVestingTermsClick,
			hasEditorPermission,
			hasKycAdminPermission,
			footerStats,
			disableSendInvestorInvitation,
			pathname,
			search,
		]
	);

	const { columnVisibility, handleColumnVisibilityChange } = useTableColPreferences({
		storageKey: StorageKeys.MIP_MANAGE_INVESTMENTS_TABLE_COLS,
		defaultColumnVisibility: {
			[ManageInvestmentsTableColumn.investmentWorkflowId]: false,
			[ManageInvestmentsTableColumn.investorId]: false,
		},
	});
	const initialQuickFilters = {
		isReleased: false,
	};

	const { mantineTableDataOperations, dataOperations } =
		useMantineTableWithPaginationFilteringSorting<
			GetInvestmentsForMipItem,
			InvestmentsListQueryDto
		>(
			columns,
			{
				initialQuickFilters,
			},
			true
		);

	const [listRequestParams, setListRequestParams] = useState<InvestmentsListQueryDto>(() => ({
		filter: mapInvestmentsTableFilterToDto(dataOperations),
		pagination: dataOperations.pagination,
		sortBy: dataOperations.sortBy,
	}));

	const { data, isLoading: investmentsLoading } = useQuery({
		queryKey: [
			formatUnicorn(
				API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_MIP_INVESTMENTS_LIST_POST,
				{ mipId }
			),
			listRequestParams,
		],
		queryFn: () =>
			getInvestments(
				formatUnicorn(
					API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_MIP_INVESTMENTS_LIST_POST,
					{ mipId }
				),
				listRequestParams
			),
		enabled: !props?.forDetailsNavigation,
	});

	const navigationRequestParams = {
		filter: listRequestParams.filter,
		search: listRequestParams.search,
		sortBy: listRequestParams.sortBy,
	};
	const { data: orderedInvestmentIds = [] } = useQuery({
		queryKey: [
			formatUnicorn(
				API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_MIP_INVESTMENTS_ORDERED_IDS_POST,
				{ mipId }
			),
			navigationRequestParams,
		],
		queryFn: () =>
			getInvestmentOrderedIds(
				formatUnicorn(
					API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_MIP_INVESTMENTS_ORDERED_IDS_POST,
					{ mipId }
				),
				navigationRequestParams
			),
		enabled: !!props?.forDetailsNavigation,
	});

	useEffect(() => {
		const filter = mapInvestmentsTableFilterToDto(
			dataOperations,
			props?.eligibilityFilter || undefined
		);
		const sortBy: SortDto[] =
			dataOperations.sortBy?.map((sort) => {
				if (sort.key === ManageInvestmentsTableColumn.investor) {
					return { key: 'displayName', desc: sort.desc };
				}
				return sort;
			}) ?? [];

		setListRequestParams({
			pagination: { take: 50, ...dataOperations.pagination },
			filter,
			search: dataOperations.search,
			sortBy,
		});
	}, [mipId, dataOperations, props?.eligibilityFilter]);

	useEffect(() => {
		setFooterStats(data?.stats);
	}, [data?.stats]);

	return {
		columns,
		investments: data?.investments ?? [],
		orderedInvestmentIds,
		rowCount: data?.totalCount,
		investmentsLoading,
		hasEditorPermission,
		columnVisibility,
		handleColumnVisibilityChange,
		mantineTableDataOperations,
		dataOperations,
		disableSendInvestorInvitation,
	};
};
